import type React from "react";
import { useMemo } from "react";
import { Html5Audio, interpolate, Sequence, useVideoConfig } from "remotion";
import { AudioTrack } from "../../types/schema";
import { CROSSFADE_DURATION_FRAMES } from "../constants";

interface AudioEngineProps {
	audioTracks: AudioTrack[];
}

export const AudioEngineResolved: React.FC<AudioEngineProps> = ({
	audioTracks,
}) => {
	const { fps } = useVideoConfig();

	const tracks = useMemo(() => {
		let currentStart = 0;
		return audioTracks.map((track, index) => {
			// Default to 900 frames (30s at 30fps) if duration is missing/undefined
			const durationSeconds = track.durationInSeconds ?? 30;
			const duration = Math.ceil(durationSeconds * fps);

			const startFrame = currentStart;
			// Overlap tracks by subtracting crossfade duration from the next start time
			// ONLY if this is NOT the last track
			const isLastTrack = index === audioTracks.length - 1;
			currentStart += isLastTrack
				? duration
				: duration - CROSSFADE_DURATION_FRAMES;
			return { track, startFrame, duration, index };
		});
	}, [audioTracks, fps]);

	return (
		<>
			{tracks.map((t, i) => {
				const isFirst = i === 0;
				const isLast = i === tracks.length - 1;

				return (
					<Sequence
						key={i}
						from={t.startFrame}
						durationInFrames={t.duration}
						layout="none" // Important so it doesn't affect visual layout
					>
						<Html5Audio
							src={t.track.url}
							volume={(f) => {
								// f is the frame relative to the start of this Sequence

								// Fade In (except for the first song)
								const fadeIn = isFirst
									? 1
									: interpolate(f, [0, CROSSFADE_DURATION_FRAMES], [0, 1], {
											extrapolateRight: "clamp",
										});

								// Fade Out (except for the last song)
								const fadeOut = isLast
									? 1
									: interpolate(
											f,
											[t.duration - CROSSFADE_DURATION_FRAMES, t.duration],
											[1, 0],
											{
												extrapolateLeft: "clamp",
											},
										);

								return fadeIn * fadeOut;
							}}
						/>
					</Sequence>
				);
			})}
		</>
	);
};
